import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useHaptics } from '../../hooks/useHaptics';
import { authMessage } from '../../components/auth/parts';
import brandIcon from '../../assets/clash-icon.svg';
import welcomeCss from './welcome.css?raw';
import welcomeHtml from './welcome.html?raw';
import { prepareWelcomeHtml } from './prepareWelcomeHtml';
import { APP_STORE_URL } from '../../lib/appStore';

export { APP_STORE_URL };
export const SUPPORT_EMAIL = 'support@headtohead.dk';

/** Map CTA copy / hrefs from the design export to real app routes. */
function actionForLink(href: string, text: string):
  | 'signup'
  | 'login'
  | 'email'
  | 'apple'
  | 'google'
  | 'app-store'
  | null {
  const t = text.replace(/\s+/g, ' ').trim().toLowerCase();

  if (t.includes('continue with email')) return 'email';
  if (t === 'log in') return 'login';
  if (t.includes('google')) return 'google';
  if (t.includes('app store') || t.includes('get the ios app') || t.includes('download on the'))
    return 'app-store';
  if (t.includes('apple') && !t.includes('app store')) return 'apple';

  if (
    href === '#play' ||
    t.includes('create free account') ||
    t.includes('play world cup') ||
    t.includes('join the world cup') ||
    t.includes('play in browser') ||
    t.includes('open in this browser') ||
    t.includes('play on android') ||
    t.includes('play on desktop')
  ) {
    return 'signup';
  }

  return null;
}

export default function LandingPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { signInWithGoogle, signInWithApple } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const html = useMemo(() => prepareWelcomeHtml(welcomeHtml, brandIcon), []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    async function onClick(e: MouseEvent) {
      const link = (e.target as Element).closest('a');
      if (!link) return;

      const href = link.getAttribute('href') ?? '';
      const text = link.textContent ?? '';

      // Static legal pages and external targets.
      if (href.endsWith('.html') || href.startsWith('mailto:') || href.startsWith('http')) return;

      // In-page anchors scroll normally.
      if (href.startsWith('#') && href !== '#play') return;

      const action = actionForLink(href, text);
      if (!action) return;

      e.preventDefault();
      if (busy && (action === 'apple' || action === 'google')) return;

      impact('medium');

      if (action === 'login') {
        navigate('/login');
        return;
      }
      if (action === 'email') {
        navigate('/signup');
        return;
      }
      if (action === 'signup') {
        navigate('/onboarding');
        return;
      }
      if (action === 'app-store') {
        window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
        return;
      }

      setError(null);
      setBusy(true);
      try {
        if (action === 'apple') await signInWithApple();
        if (action === 'google') await signInWithGoogle();
      } catch (err) {
        setError(authMessage(err));
        setBusy(false);
      }
    }

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [busy, impact, navigate, signInWithApple, signInWithGoogle]);

  return (
    <>
      <style>{welcomeCss}</style>
      {error && (
        <div className="fixed inset-x-4 top-4 z-[calc(var(--z-toast))] mx-auto max-w-md rounded-xl bg-miss/10 px-4 py-3 text-center text-[13px] font-semibold text-danger shadow-card">
          {error}
        </div>
      )}
      <div
        ref={rootRef}
        className="landing-root landing-scroll native-scroll h-full overflow-y-auto overflow-x-hidden"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}
